import styled from 'styled-components';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Interweave } from 'interweave';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataPoint, ProcedureInterface, QuestionInterface } from '../../types';
import { isSyntheticQuestionType, NormalQuestionType } from '../../enums';
import UnmappedRecord from '../../types/UnmappedRecord';

const MultiRecordTable = styled(Table)`
    table-layout: fixed;
`;

const MultiRecordHeaderColumn = styled(TableCell)<{ $short?: boolean }>(({ theme, $short }) => `
    font-family: ${theme.typography.fontFamily} !important;
    font-size: 14px;
    font-weight: 600 !important;
    color: ${theme.palette.grey[900]} !important;
    background-color: ${theme.palette.grey[200]};
    border-bottom: 1px solid ${theme.palette.grey[300]};
    padding: 14px 15px !important;
    ${$short ? 'width: 10%;' : ''};
    
    .disabled {
      color: ${theme.palette.grey[600]};
    }
`);

const MultiRecordRow = styled(TableRow)(({ theme }) => `
    border-bottom: 1px solid ${theme.palette.grey[300]};

  .MuiTableCell-root {
      font-size: 12px;
      font-family: ${theme.typography.fontFamily};
      padding: 15px 15px;
      font-weight: 400;
      color: ${theme.palette.grey[900]};
      box-sizing: border-box;
      
      &.text-truncated {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
       }
       
       &.text-wrapped {
        white-space: normal;
        word-break: break-word;
       }
  }
    
  .chevron-icon {
      color:  ${theme.palette.grey[700]};
      font-weight: bold;
      font-size: 14px;
      width: 10%;
      text-align: right;
  }
  
  .expanded-record {
      background-color: #FBFAF4;
      border-color: #E9E2C0;
      font-size: 14px;
      padding-top: 12px;
      padding-bottom: 12px;
  
      &.question-text {
          font-weight: 500;
      }
    
      &.answer-text {
          font-weight: 400;
      }
     
      &.record-header {
          font-weight: 500;
          background-color: #F4F0DF;
          text-transform: uppercase;
      }
     
      &.first {
          width: 50%;
      }
  }
`);

function getQuestionVariableToAnswerMap(dataPoints: DataPoint[]) {
  return dataPoints.reduce((map, dataPoint) => {
    const variableName = dataPoint.question_variable_name;
    if (variableName) {
      return { ...map, [variableName]: dataPoint.answer };
    }
    return map;
  }, {} as Record<string, any>);
}

interface TraitsMultiRecordProps {
  procedureConfig: ProcedureInterface;
  unmappedRecord: UnmappedRecord;
}

export default function MultiRecord({ procedureConfig, unmappedRecord }: TraitsMultiRecordProps) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  const questionVariableToAnswerMap = getQuestionVariableToAnswerMap(unmappedRecord.dataPoints);
  return (
    <TableContainer>
      <MultiRecordTable data-testid="multi-record-table-header">
        <TableHead>
          <TableRow>
            {
              procedureConfig.questions.filter(({ type }) => !isSyntheticQuestionType(type))
                .slice(0, 4)
                .map((question) => {
                  const { questionId, questionText } = question;
                  return (
                    <MultiRecordHeaderColumn key={questionId} data-testid={`multi-record-question-${questionId}`}>
                      <Interweave className={`${expanded ? 'disabled' : ''}`} content={questionText} />
                    </MultiRecordHeaderColumn>
                  );
                })
            }
            <MultiRecordHeaderColumn $short />
          </TableRow>
        </TableHead>
        {!expanded
            && (
            <TableBody>
              <MultiRecordRow data-testid="multi-record-answer-row" onClick={() => setExpanded(true)}>
                {
                  procedureConfig.questions.filter(({ type }) => !isSyntheticQuestionType(type))
                    .slice(0, 4)
                    .map((question) => {
                      const { questionId, variableName } = question;
                      if (!variableName) {
                        return <TableCell key={questionId} />;
                      }
                      const answer = questionVariableToAnswerMap[variableName];
                      return <TableCell className="text-truncated" key={variableName} data-testid={`multi-record-answer-${variableName}`}>{ answer }</TableCell>;
                    })
                }
                <TableCell className="chevron-icon">
                  <FontAwesomeIcon icon={faChevronDown as IconProp} />
                </TableCell>
              </MultiRecordRow>
            </TableBody>
            )}
      </MultiRecordTable>
      {expanded
          && (
          <MultiRecordTable data-testid="multi-record-table-data">
            <TableBody>
              <MultiRecordRow onClick={() => setExpanded(false)}>
                <TableCell className="expanded-record record-header first">{t('Procedure.MultiRecord.Record')}</TableCell>
                <TableCell className="expanded-record record-header" />
                <TableCell className="expanded-record record-header chevron-icon">
                  <FontAwesomeIcon icon={faChevronUp as IconProp} />
                </TableCell>
              </MultiRecordRow>
              {procedureConfig.questions.filter(({ type }) => !isSyntheticQuestionType(type))
                .map(({
                  variableName, questionId, questionText, type,
                }: QuestionInterface) => {
                  if (!variableName) {
                    return <TableCell key={questionId} />;
                  }
                  const answer = questionVariableToAnswerMap[variableName];
                  return (
                    <MultiRecordRow key={variableName}>
                      <TableCell className="expanded-record question-text" colSpan={1} data-testid={`multi-record-question-${variableName}`}>{questionText}</TableCell>
                      <TableCell className="expanded-record answer-text text-wrapped" colSpan={2} data-testid={`multi-record-answer-${variableName}`}>
                        {
                                type === NormalQuestionType.MULTI_SELECT && typeof answer === 'string'
                                  ? (
                                    <ul className="Answers">
                                      {(answer as string).split('\n').map((item) => (<li key={item}>{item}</li>))}
                                    </ul>
                                  )
                                  : answer
                              }
                      </TableCell>
                    </MultiRecordRow>
                  );
                })}
            </TableBody>
          </MultiRecordTable>
          )}
    </TableContainer>
  );
}
